import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { useToast } from "../components/ui/ToastContainer";
import { parseApiError } from "../utils/errorHandler";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Edit2,
  Globe,
  FileLock,
} from "lucide-react";
import { quizService } from "../services/quizService";
import { courseService } from "../services/courseService";
import { QuestionForm } from "../components/exams/QuestionForm";
import { SearchableSelect } from "../components/ui/SearchableSelect";

// Mocking these services if not available or just assuming they exist
// I'll leave them commented and use placeholder inputs for now or fetch if I can validation

export default function AddEditExamPage() {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // details, questions, settings

  const [exam, setExam] = useState({
    title: "",
    description: "",
    course: "", // ID
    duration: 60,
    passingScore: 70,
    isPublished: false,
    questions: [] as any[], // List of question IDs or objects if I populate
    // New Fields
    maxAttempts: 1 as number | null,
    instructions: "",
    shuffleQuestions: false,
    shuffleAnswers: false,
    showResultsImmediately: true,
    showCorrectAnswers: true,
    showExplanations: true,
    allowReviewBeforeSubmit: true,
    isMandatory: false,
    requiresProctoring: false,
    allowCalculator: false,
    allowNotes: false,
    certificateOnPass: false,
    availableFrom: "",
    availableUntil: "",
    source: "internal",
    externalReference: "",
  });

  const [questionsList, setQuestionsList] = useState<any[]>([]); // To hold full question details
  const [editingQuestion, setEditingQuestion] = useState<any>(null); // If set, show QuestionForm
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  const [courses, setCourses] = useState<any[]>([]);

  useEffect(() => {
    fetchInitialData();
    if (isEditMode) {
      fetchExam();
    }
  }, [id]);

  const fetchInitialData = async () => {
    try {
      const [coursesRes] = await Promise.all([courseService.getAllCourses()]);

      // Courses use { success: true, data: { courses: [] } }
      if (coursesRes.success && coursesRes.data?.courses) {
        setCourses(
          coursesRes.data.courses.map((c: any) => ({
            value: c._id,
            label: c.title,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch initial data", error);
      parseApiError(error);
      showWarning("Failed to load some dropdown data", "Warning");
    }
  };

  const fetchExam = async () => {
    try {
      const response = await quizService.getQuizById(id as string);
      if (response && response.status === "success") {
        const quizData = (response as any).data.quiz;
        setExam({
          title: quizData.title,
          description: quizData.description || "",
          course: quizData.course?._id || quizData.course,
          duration: quizData.duration,
          passingScore: quizData.passingScore,
          isPublished: quizData.isPublished,
          questions: quizData.questions || [],
          maxAttempts: quizData.maxAttempts,
          instructions: quizData.instructions || "",
          shuffleQuestions: quizData.shuffleQuestions || false,
          shuffleAnswers: quizData.shuffleAnswers || false,
          showResultsImmediately: quizData.showResultsImmediately ?? true,
          showCorrectAnswers: quizData.showCorrectAnswers ?? true,
          showExplanations: quizData.showExplanations ?? true,
          allowReviewBeforeSubmit: quizData.allowReviewBeforeSubmit ?? true,
          isMandatory: quizData.isMandatory || false,
          requiresProctoring: quizData.requiresProctoring || false,
          allowCalculator: quizData.allowCalculator || false,
          allowNotes: quizData.allowNotes || false,
          certificateOnPass: quizData.certificateOnPass || false,
          availableFrom: quizData.availableFrom
            ? new Date(quizData.availableFrom).toISOString().slice(0, 16)
            : "",
          availableUntil: quizData.availableUntil
            ? new Date(quizData.availableUntil).toISOString().slice(0, 16)
            : "",
          source: quizData.source || "internal",
          externalReference: quizData.externalReference || "",
        });

        // Fetch questions details
        const qResponse = await quizService.getQuizQuestions(
          id as string,
          true
        ); // true for include answers
        if (qResponse && qResponse.status === "success") {
          setQuestionsList((qResponse as any).data.questions);
        }
      }
    } catch (error) {
      console.error("Failed to fetch exam", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Failed to Load Exam");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExam = async () => {
    setLoading(true);
    try {
      const payload = {
        ...exam,
        availableFrom: exam.availableFrom || null,
        availableUntil: exam.availableUntil || null,
        courseId: exam.course, // Adjusted to match service type if needed
      };

      if (isEditMode && id) {
        await quizService.updateQuiz(id, payload);
        showSuccess("Exam updated successfully", "Success");
      } else {
        const response = await quizService.createQuiz(payload as any);
        if (response.status === "success") {
          const newId = (response as any).data.quiz._id;
          showSuccess("Exam created successfully", "Success");
          navigate(`/exams/${newId}/edit`, { replace: true });
        }
      }
    } catch (error) {
      console.error("Save failed", error);
      const apiError = parseApiError(error);
      showError(
        apiError.message,
        isEditMode ? "Update Failed" : "Create Failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!id) {
      showWarning("Please save the exam first.", "Cannot Publish");
      return;
    }
    setLoading(true);
    try {
      const response = await quizService.togglePublishQuiz(id);
      if (
        response &&
        (response.status === "success" || response.status === "true")
      ) {
        setExam((prev) => ({ ...prev, isPublished: !prev.isPublished }));
        showSuccess(
          `Exam ${
            !exam.isPublished ? "published" : "unpublished"
          } successfully`,
          "Success"
        );
      }
    } catch (error) {
      console.error("Failed to toggle publish", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Publish Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async (questionData: any) => {
    try {
      if (editingQuestion) {
        // Update existing question
        await quizService.updateQuestion(editingQuestion._id, questionData);
        setQuestionsList((prev) =>
          prev.map((q) =>
            q._id === editingQuestion._id ? { ...questionData, _id: q._id } : q
          )
        );
      } else {
        // Create new question
        if (!isEditMode || !id) {
          showWarning(
            "Please save the exam first before adding questions.",
            "Cannot Add Question"
          );
          return;
        }
        const response = await quizService.addQuestionToQuiz(id, questionData);
        if (response.status === "success") {
          setQuestionsList((prev) => [
            ...prev,
            (response as any).data.question,
          ]);
        }
      }
      showSuccess(
        editingQuestion
          ? "Question updated successfully"
          : "Question added successfully",
        "Success"
      );
      setShowQuestionForm(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error("Failed to save question", error);
      const apiError = parseApiError(error);
      showError(apiError.message, "Question Save Failed");
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await quizService.deleteQuestion(questionId);
      setQuestionsList((prev) => prev.filter((q) => q._id !== questionId));
      showSuccess("Question deleted successfully", "Success");
    } catch (e) {
      console.error(e);
      const apiError = parseApiError(e);
      showError(apiError.message, "Delete Failed");
    }
  };

  if (loading && !exam.title && isEditMode) return <div>Loading...</div>;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6 pb-20 p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/exams" className="p-2 hover:bg-gray-100 rounded-full">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-2xl font-bold text-[#102040]">
              {isEditMode ? "Edit Exam" : "Create New Exam"}
            </h1>
          </div>
          <div className="flex flex-col sm:flex-row w-full sm:w-auto items-center gap-3">
            {isEditMode && (
              <Button
                variant="outline"
                onClick={handleTogglePublish}
                className={`w-full sm:w-auto flex items-center justify-center gap-2 ${
                  exam.isPublished
                    ? "text-amber-600 border-amber-200 hover:bg-amber-50"
                    : "text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                {exam.isPublished ? (
                  <>
                    <FileLock size={18} />
                    Unpublish
                  </>
                ) : (
                  <>
                    <Globe size={18} />
                    Publish Exam
                  </>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/exams")}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveExam}
              className="w-full sm:w-auto bg-[#102040] text-white flex items-center justify-center gap-2"
            >
              <Save size={18} />
              Save Exam
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 overflow-x-auto hide-scrollbar">
          <div className="flex gap-6 min-w-max">
            <button
              onClick={() => setActiveTab("details")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "details"
                  ? "border-[#102040] text-[#102040]"
                  : "border-transparent text-gray-500"
              }`}
            >
              Exam Details
            </button>
            <button
              onClick={() => setActiveTab("questions")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "questions"
                  ? "border-[#102040] text-[#102040]"
                  : "border-transparent text-gray-500"
              }`}
            >
              Questions ({questionsList.length})
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "settings"
                  ? "border-[#102040] text-[#102040]"
                  : "border-transparent text-gray-500"
              }`}
            >
              Settings
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === "details" && (
          <Card className="p-4 sm:p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Exam Title
                </label>
                <Input
                  value={exam.title}
                  onChange={(e) => setExam({ ...exam, title: e.target.value })}
                  placeholder="e.g. Final Assessment"
                />
              </div>

              <SearchableSelect
                label="Topic / Course"
                placeholder="Select Course"
                options={courses}
                value={exam.course}
                onChange={(value) => setExam({ ...exam, course: value })}
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Duration (Minutes)
                </label>
                <Input
                  type="number"
                  value={exam.duration}
                  onChange={(e) =>
                    setExam({ ...exam, duration: Number(e.target.value) })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Passing Score (%)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={exam.passingScore}
                  onChange={(e) =>
                    setExam({ ...exam, passingScore: Number(e.target.value) })
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Max Attempts (Leave empty for unlimited)
                </label>
                <Input
                  type="number"
                  min="1"
                  value={exam.maxAttempts || ""}
                  onChange={(e) =>
                    setExam({
                      ...exam,
                      maxAttempts: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
                value={exam.description}
                onChange={(e) =>
                  setExam({ ...exam, description: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Instructions for Students
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
                value={exam.instructions}
                onChange={(e) =>
                  setExam({ ...exam, instructions: e.target.value })
                }
                placeholder="e.g. Please read carefully before starting..."
              />
            </div>
          </Card>
        )}

        {activeTab === "questions" && (
          <div className="space-y-4">
            {!showQuestionForm ? (
              <>
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Questions</h3>
                  <Button
                    onClick={() => {
                      setEditingQuestion(null);
                      setShowQuestionForm(true);
                    }}
                    className="bg-[#102040] text-white flex items-center gap-2"
                  >
                    <Plus size={16} /> Add Question
                  </Button>
                </div>

                <div className="space-y-3">
                  {questionsList.length === 0 ? (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                      <p className="text-gray-500">No questions added yet.</p>
                    </div>
                  ) : (
                    questionsList.map((q, idx) => (
                      <div
                        key={q._id}
                        className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex items-start justify-between group"
                      >
                        <div className="flex gap-3">
                          <span className="shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">
                              {q.questionText || q.text}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                                {q.questionType || q.type}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                                {q.marks} pts
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingQuestion(q);
                              setShowQuestionForm(true);
                            }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(q._id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <QuestionForm
                initialData={editingQuestion}
                onSave={handleSaveQuestion}
                onCancel={() => setShowQuestionForm(false)}
              />
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <Card className="p-4 sm:p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Scheduling */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">
                  Scheduling
                </h3>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Available From
                  </label>
                  <Input
                    type="datetime-local"
                    value={exam.availableFrom}
                    onChange={(e) =>
                      setExam({ ...exam, availableFrom: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Available Until
                  </label>
                  <Input
                    type="datetime-local"
                    value={exam.availableUntil}
                    onChange={(e) =>
                      setExam({ ...exam, availableUntil: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Time Limit (Minutes)
                  </label>
                  <Input
                    type="number"
                    value={exam.duration}
                    onChange={(e) =>
                      setExam({ ...exam, duration: Number(e.target.value) })
                    }
                  />
                </div>
              </div>

              {/* Display & Behavior */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">
                  Behavior
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Shuffle Questions
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.shuffleQuestions}
                      onChange={(e) =>
                        setExam({ ...exam, shuffleQuestions: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Shuffle Answers
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.shuffleAnswers}
                      onChange={(e) =>
                        setExam({ ...exam, shuffleAnswers: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Review Before Submit
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.allowReviewBeforeSubmit}
                      onChange={(e) =>
                        setExam({
                          ...exam,
                          allowReviewBeforeSubmit: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">
                  Results & Grading
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Show Results Immediately
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.showResultsImmediately}
                      onChange={(e) =>
                        setExam({
                          ...exam,
                          showResultsImmediately: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Show Correct Answers
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.showCorrectAnswers}
                      onChange={(e) =>
                        setExam({
                          ...exam,
                          showCorrectAnswers: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Show Explanations
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.showExplanations}
                      onChange={(e) =>
                        setExam({ ...exam, showExplanations: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Certificate on Pass
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.certificateOnPass}
                      onChange={(e) =>
                        setExam({
                          ...exam,
                          certificateOnPass: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Access Control */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-b pb-2">
                  Access Control
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Mandatory</span>
                    <input
                      type="checkbox"
                      checked={exam.isMandatory}
                      onChange={(e) =>
                        setExam({ ...exam, isMandatory: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Requires Proctoring
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.requiresProctoring}
                      onChange={(e) =>
                        setExam({
                          ...exam,
                          requiresProctoring: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      Allow Calculator
                    </span>
                    <input
                      type="checkbox"
                      checked={exam.allowCalculator}
                      onChange={(e) =>
                        setExam({ ...exam, allowCalculator: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Allow Notes</span>
                    <input
                      type="checkbox"
                      checked={exam.allowNotes}
                      onChange={(e) =>
                        setExam({ ...exam, allowNotes: e.target.checked })
                      }
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Source
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={exam.source}
                  onChange={(e) => setExam({ ...exam, source: e.target.value })}
                >
                  <option value="internal">Internal</option>
                  <option value="pmi">PMI</option>
                  <option value="pmbok">PMBOK</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  External Reference
                </label>
                <Input
                  value={exam.externalReference}
                  onChange={(e) =>
                    setExam({ ...exam, externalReference: e.target.value })
                  }
                  placeholder="e.g. PMBOK 7th Ed, pg 123"
                />
              </div>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
